import React, { useState, useCallback } from 'react';

import { Outlet, useMatches } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Header } from '@/components/molecules/Header';
import { getHeaderConfig, getFooterConfig, renderContactModal } from './config';
import { HEADER_DATA } from './const';
import { useClientConfig } from '@/contexts/ClientConfigContext';
import { useLoan } from '@/contexts';
import { configIcons } from '@/assets/icons';
import { RouteHandleKey, RouteHandle } from '@/utils';
import { useSnackbar } from '@/hooks';

/**
 * Common layout for QuickLoan flow pages
 * Provides consistent structure and styling for all QuickLoan routes
 * Header shown on all pages, with different variants:
 * - Landing/Setup pages: rounded corners with profile avatar
 * - Application page: rectangular without profile avatar
 */
const QuickLoanLayout: React.FC = () => {
  const matches = useMatches();
  const theme = useTheme();
  const { t } = useTranslation();
  const { clientConfig } = useClientConfig();
  const { applicationData } = useLoan();
  const { showSnackbar } = useSnackbar();

  // Contact modal state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Handler for clipboard operations in contact modal
  const handleCopyToClipboard = useCallback(
    (success: boolean, errorMessage?: string) => {
      if (success) {
        showSnackbar(t('common.copiedToClipboard', 'Copied to clipboard'), {
          variant: 'success',
          autoHideDuration: 2000,
        });
      } else {
        showSnackbar(
          errorMessage || t('common.copyFailed', 'Failed to copy to clipboard'),
          {
            variant: 'error',
            autoHideDuration: 3000,
          }
        );
      }
    },
    [showSnackbar, t]
  );

  // Handler for contact button click - opens contact modal
  const handleContactClick = () => {
    setIsContactModalOpen(true);
  };

  // Get the current route handle key from matched routes
  const currentHandle = matches.find(
    (match) => (match.handle as RouteHandle)?.key
  )?.handle as RouteHandle | undefined;
  const routeKey = currentHandle?.key;

  // Check if current page is LoanOfficer, LandingLoanOfficer, LoanSetup or Disclosure (show footer)
  const isLandingOrSetup =
    routeKey === RouteHandleKey.LOAN_OFFICER ||
    routeKey === RouteHandleKey.LANDING_LOAN_OFFICER ||
    routeKey === RouteHandleKey.LOAN_SETUP ||
    routeKey === RouteHandleKey.DISCLOSURE;

  // Show Login button only on loan officer selection page
  const isLoanOfficerPage = routeKey === RouteHandleKey.LOAN_OFFICER;

  // Show Login button only till landing-loan-officer page
  const shouldShowLogin =
    isLoanOfficerPage || routeKey === RouteHandleKey.LANDING_LOAN_OFFICER;

  // Determine header content based on flow
  const selectedOfficerId = applicationData.loanOfficerId;
  const selectedOfficerName = applicationData.loanOfficerName;
  const selectedOfficerNmls = applicationData.loanOfficerNmlsId;

  // After landing-loan-officer, show appropriate header (includes setup page)
  const isAfterLoanOfficerSelection = routeKey === RouteHandleKey.LOAN_SETUP;

  let headerLoanOfficer = HEADER_DATA.loanOfficer;
  let headerNmls = HEADER_DATA.nmls;
  let displayName: string | undefined;
  let loanOfficerPhoto: string | undefined;
  let isLoanOfficerSelected = true; // Default to true (loan officer selected)

  if (isAfterLoanOfficerSelection) {
    if (selectedOfficerId && selectedOfficerName) {
      // Loan officer was selected - use data from API stored in context
      headerLoanOfficer = selectedOfficerName;
      headerNmls = selectedOfficerNmls
        ? `#${selectedOfficerNmls}`
        : HEADER_DATA.nmls;
      displayName = undefined; // Show as loan officer
      loanOfficerPhoto = applicationData.loanOfficerData?.photo || undefined;
      isLoanOfficerSelected = true;
    } else {
      // "I don't want" was clicked - show Revolution Mortgage
      headerLoanOfficer = HEADER_DATA.companyName;
      // Use branch NMLS from context if available (already includes # prefix from mapper)
      const branchNmls = applicationData.branchData?.branchNmls;
      headerNmls = branchNmls ? branchNmls : HEADER_DATA.companyNmls;
      displayName = HEADER_DATA.companyName; // Show as company
      loanOfficerPhoto = configIcons.revolutionProfileLogo; // Use profile logo
      isLoanOfficerSelected = false; // No loan officer selected
    }
  }

  // Determine header variant: 'landing' for all pages
  const headerVariant = 'landing';

  const headerConfig = getHeaderConfig({
    theme,
    logo: clientConfig.logoSrc,
    loanOfficer: headerLoanOfficer,
    nmls: headerNmls,
    showLogin: shouldShowLogin,
    displayName,
    loanOfficerPhoto,
    isProfileLogo: loanOfficerPhoto === configIcons.revolutionProfileLogo,
    variant: headerVariant,
    t,
    isLoanOfficerSelected,
    onContactClick: isAfterLoanOfficerSelection
      ? handleContactClick
      : undefined,
  });

  // Show header on all QuickLoan pages (landing, setup, and disclosure)
  const shouldShowHeader = isLandingOrSetup;

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, #F3F7FF 0%, #F9FAFC 100%)',
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header - Show on all QuickLoan pages with appropriate variant */}
      {shouldShowHeader && <Header {...headerConfig} />}

      {/* Main content area - Full height */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          display: 'flex',
        }}
      >
        {/* This renders the current route's component */}
        <Outlet />
      </Box>

      {/* Footer - Only show on LoanOfficer, Landing, LoanSetup and Disclosure pages */}
      {isLandingOrSetup &&
        getFooterConfig({
          theme,
          t,
          loanOfficerData: applicationData.loanOfficerData
            ? {
                name: applicationData.loanOfficerData.name,
                email: applicationData.loanOfficerData.email,
                phone: applicationData.loanOfficerData.phone,
                nmls: applicationData.loanOfficerData.nmls,
                title: applicationData.loanOfficerData.title ?? undefined,
              }
            : undefined,
          branchData: applicationData.branchData
            ? {
                name: applicationData.branchData.name,
                branchNmls: applicationData.branchData.branchNmls,
                email: applicationData.branchData.email,
                phone: applicationData.branchData.phone,
              }
            : undefined,
        })}

      {/* Contact Modal - rendered via config function */}
      {renderContactModal({
        theme,
        open: isContactModalOpen,
        onClose: () => setIsContactModalOpen(false),
        loanOfficerData: applicationData.loanOfficerData,
        branchData: applicationData.branchData,
        onCopyToClipboard: handleCopyToClipboard,
      })}
    </Box>
  );
};

export default QuickLoanLayout;
